/**
 * F-003 — Toast helper.
 *
 * Components fire `toast.success("…")` / `toast.error(…)` after an action
 * completes. The host App.tsx mounts a `<ToastContainer />` and listens
 * for the `moderation:toast` event, pushing each message into the queue.
 *
 * Toasts also re-fire `announce()` so screen-reader users keep their
 * existing live region behaviour — sighted users get the visual cue,
 * SR users get the announcement; both flow from the same call site.
 *
 * Lives in its own module so test files can import the helpers without
 * dragging in the full App tree (and the Cognito SDK).
 */
import { announce } from './announce';

export const TOAST_EVENT = 'moderation:toast';

export type ToastTone = 'success' | 'error' | 'info';

export interface ToastDetail {
  id: string;
  message: string;
  tone: ToastTone;
  /** Milliseconds before the toast auto-dismisses. 0 means stick. */
  durationMs: number;
}

let counter = 0;

function emit(detail: ToastDetail): void {
  if (typeof window === 'undefined') return;
  window.dispatchEvent(new CustomEvent(TOAST_EVENT, { detail }));
}

function newId(): string {
  counter += 1;
  return `toast-${Date.now()}-${counter}`;
}

export const toast = {
  success(message: string, options: { durationMs?: number; announce?: boolean } = {}): void {
    emit({
      id: newId(),
      message,
      tone: 'success',
      durationMs: options.durationMs ?? 4000,
    });
    if (options.announce !== false) announce(message);
  },
  error(message: string, options: { durationMs?: number; announce?: boolean } = {}): void {
    emit({
      id: newId(),
      message,
      tone: 'error',
      // Errors stick a bit longer so the user can read them.
      durationMs: options.durationMs ?? 6000,
    });
    if (options.announce !== false) announce(message);
  },
  info(message: string, options: { durationMs?: number; announce?: boolean } = {}): void {
    emit({
      id: newId(),
      message,
      tone: 'info',
      durationMs: options.durationMs ?? 4000,
    });
    if (options.announce !== false) announce(message);
  },
};
